import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import AppShell from '../chrome/AppShell';
import { PageHeadActions } from '../components/nav';
import { TCard, TFoot, TRow } from '../components/table';
import { ListState, useList } from '../components/roster';
import useProduction from '@/src/Production/components/hooks/useProduction';

/* Design — read-only request list grouped by status, with deliverable progress. */

const STATUS_ORDER = [
    'needs_artwork',
    'up_next',
    'to_do',
    'doing',
    'waiting_on_files',
    'client_designing',
    'pending_internal_approval',
    'sent_to_client',
    'client_changes',
    'ready_for_production',
    'archived',
];

const statusLabel = (s: string) => {
    const t = s.replaceAll('_', ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
};

const designer = (r: any) =>
    r.designerFirstName ? [r.designerFirstName, r.designerLastName].filter(Boolean).join(' ') : 'Unassigned';

const due = (r: any) => {
    if (!r.dueDate) return 'No due date';
    const d = parseISO(String(r.dueDate));
    return Number.isNaN(d.getTime()) ? 'No due date' : `Due ${format(d, 'MMM d')}`;
};

export default function DesignV3() {
    const navigate = useNavigate();
    const { getDesignRequests } = useProduction();
    const list = useList<any>(() => getDesignRequests());
    const rows = list.rows ?? [];

    const groups = STATUS_ORDER.map((status) => ({
        status,
        rows: rows.filter((r) => r.status === status),
    })).filter((g) => g.rows.length > 0);
    const shown = groups.reduce((n, g) => n + g.rows.length, 0);

    const open = (r: any) => {
        if (r.activationId) navigate(`/activations/${r.activationId}`);
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Design"
                sub={list.rows ? `${shown} ${shown === 1 ? 'request' : 'requests'}` : 'Design requests by status'}
            />
            <ListState
                rows={list.rows}
                failed={list.failed}
                what="design requests"
                reload={list.reload}
                empty={shown === 0 ? 'No design requests.' : undefined}
            >
                {groups.map((g, gi) => (
                    <React.Fragment key={g.status}>
                        <p className="s-lab pl-group" style={gi === 0 ? { marginTop: 4 } : undefined}>
                            {statusLabel(g.status)} · {g.rows.length}
                        </p>
                        <TCard>
                            {g.rows.map((r, i) => {
                                const progress = `${r.deliveredCount ?? 0}/${r.deliverableCount ?? 0} delivered`;
                                return (
                                    <TRow
                                        key={r.id}
                                        index={i}
                                        name={r.activationName ?? 'Untitled'}
                                        badge={r.clientApprovedAt ? 'Approved' : undefined}
                                        meta={designer(r)}
                                        metaDate={due(r)}
                                        date={due(r)}
                                        status={progress}
                                        href={r.activationId ? `/activations/${r.activationId}` : undefined}
                                        onOpen={() => open(r)}
                                        menuItems={[]}
                                    />
                                );
                            })}
                        </TCard>
                    </React.Fragment>
                ))}
            </ListState>
            {list.rows && shown > 0 && <TFoot>{`${shown} ${shown === 1 ? 'request' : 'requests'} · soonest due first`}</TFoot>}
        </AppShell>
    );
}
